import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Utensils, ShoppingBag, Home, HeartPulse, Scale, Dumbbell,
  ArrowRight, Check, Star, TrendingUp
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import SEO from '../../components/SEO';

function IndustryRestaurants() {
  const { language } = useLanguage();

  const content = {
    sq: {
      title: 'Web Solutions për Restorante',
      subtitle: 'Rriste shitjet me website professional dhe sistem rezervimi online',
      features: [
        { icon: Utensils, title: 'Menu Online', desc: 'Menu interaktive me foto dhe çmime' },
        { icon: ShoppingBag, title: 'Online Ordering', desc: 'Porosi direkt nga website' },
        { icon: Check, title: 'Rezervime Online', desc: 'Sistem rezervimi 24/7' },
        { icon: Star, title: 'Reviews Integration', desc: 'Google & Facebook reviews' }
      ],
      benefits: [
        '40% më shumë rezervime me sistem online',
        'Zvogëlo ngarkesën e stafit me automatizim',
        'Rriste visibility në Google Maps',
        'Mobile-first design për klientët tuaj'
      ],
      caseStudy: {
        title: 'Deluxe Restaurant',
        result: '40% e rezervimeve online në 3 muaj',
        increase: '+150% traffic nga Google'
      },
      pricing: {
        title: 'Paketa për Restorante',
        price: '€1,200',
        includes: [
          'Website me design custom',
          'Menu online me fotografi',
          'Sistem rezervimi të integruar',
          'Google Maps integration',
          'Mobile responsive',
          '6 muaj maintenance falas'
        ]
      }
    },
    en: {
      title: 'Web Solutions for Restaurants',
      subtitle: 'Increase sales with professional website and online booking system',
      features: [
        { icon: Utensils, title: 'Online Menu', desc: 'Interactive menu with photos and prices' },
        { icon: ShoppingBag, title: 'Online Ordering', desc: 'Direct orders from website' },
        { icon: Check, title: 'Online Booking', desc: '24/7 reservation system' },
        { icon: Star, title: 'Reviews Integration', desc: 'Google & Facebook reviews' }
      ],
      benefits: [
        '40% more bookings with online system',
        'Reduce staff workload with automation',
        'Increase visibility on Google Maps',
        'Mobile-first design for your customers'
      ],
      caseStudy: {
        title: 'Deluxe Restaurant',
        result: '40% of bookings online in 3 months',
        increase: '+150% traffic from Google'
      },
      pricing: {
        title: 'Restaurant Package',
        price: '€1,200',
        includes: [
          'Custom design website',
          'Online menu with photography',
          'Integrated booking system',
          'Google Maps integration',
          'Mobile responsive',
          '6 months free maintenance'
        ]
      }
    }
  };

  const t = content[language];

  return (
    <>
      <SEO title={t.title} description={t.subtitle} />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-20">
        <div className="container mx-auto px-4">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto mb-16"
          >
            <Utensils className="w-16 h-16 mx-auto mb-6 text-yellow-600" />
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              {t.title}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              {t.subtitle}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-8 py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
            >
              {language === 'sq' ? 'Kërko Ofertë' : 'Get Quote'}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {t.features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700"
              >
                <feature.icon className="w-12 h-12 text-yellow-600 mb-4" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Benefits & Case Study */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                {language === 'sq' ? 'Përfitime' : 'Benefits'}
              </h2>
              <ul className="space-y-4">
                {t.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">{t.caseStudy.title}</h3>
              <div className="text-4xl font-bold mb-2">{t.caseStudy.result}</div>
              <div className="text-lg mb-6">{t.caseStudy.increase}</div>
              <Link
                to="/case-studies"
                className="inline-flex items-center gap-2 bg-white text-yellow-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all"
              >
                {language === 'sq' ? 'Shiko Case Study' : 'View Case Study'}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* Pricing */}
          <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-200 dark:border-gray-700">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-4">
              {t.pricing.title}
            </h2>
            <div className="text-center mb-8">
              <div className="text-5xl font-bold text-yellow-600">{t.pricing.price}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {language === 'sq' ? 'Projekt i plotë' : 'Complete project'}
              </div>
            </div>
            <ul className="space-y-3 mb-8">
              {t.pricing.includes.map((item, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{item}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/pricing-calculator"
              className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-8 py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all text-center flex items-center justify-center gap-2"
            >
              {language === 'sq' ? 'Llogarit Çmimin' : 'Calculate Price'}
              <TrendingUp className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

// Similar pages for other industries - simplified versions
export function IndustryEcommerce() {
  return <IndustryTemplate industry="ecommerce" icon={ShoppingBag} />;
}

export function IndustryRealEstate() {
  return <IndustryTemplate industry="realestate" icon={Home} />;
}

export function IndustryHealthcare() {
  return <IndustryTemplate industry="healthcare" icon={HeartPulse} />;
}

export function IndustryLegal() {
  return <IndustryTemplate industry="legal" icon={Scale} />;
}

export function IndustryFitness() {
  return <IndustryTemplate industry="fitness" icon={Dumbbell} />;
}

// Generic template component
function IndustryTemplate({ industry, icon: Icon }) {
  const { language } = useLanguage();
  
  const industries = {
    ecommerce: {
      title: { sq: 'E-Commerce Solutions', en: 'E-Commerce Solutions' },
      subtitle: { sq: 'Dyqan online që konverton', en: 'Online store that converts' },
      price: '€2,000'
    },
    realestate: {
      title: { sq: 'Real Estate Websites', en: 'Real Estate Websites' },
      subtitle: { sq: 'Gjenero leads për properties', en: 'Generate leads for properties' },
      price: '€1,500'
    },
    healthcare: {
      title: { sq: 'Healthcare Websites', en: 'Healthcare Websites' },
      subtitle: { sq: 'Website për klinika dhe mjekë', en: 'Websites for clinics and doctors' },
      price: '€1,800'
    },
    legal: {
      title: { sq: 'Law Firm Websites', en: 'Law Firm Websites' },
      subtitle: { sq: 'Website profesional për avokatë', en: 'Professional websites for lawyers' },
      price: '€1,600'
    },
    fitness: {
      title: { sq: 'Fitness & Gym Websites', en: 'Fitness & Gym Websites' },
      subtitle: { sq: 'Website me booking për classe', en: 'Website with class booking' },
      price: '€1,400'
    }
  };

  const data = industries[industry];

  return (
    <>
      <SEO title={data.title[language]} description={data.subtitle[language]} />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-20">
        <div className="container mx-auto px-4 text-center">
          <Icon className="w-16 h-16 mx-auto mb-6 text-yellow-600" />
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
            {data.title[language]}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            {data.subtitle[language]}
          </p>
          <div className="text-5xl font-bold text-yellow-600 mb-8">{data.price}</div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white px-8 py-4 rounded-lg font-semibold shadow-lg"
          >
            {language === 'sq' ? 'Kontaktoni' : 'Contact Us'}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </>
  );
}

export default IndustryRestaurants;